/**
 * The assistant's tool registry (docs/assistant.md).
 *
 * This is the single source of truth for what the in-product assistant can do. It is deliberately a
 * *description of existing API routes*, not a new capability surface: the server executes a tool by
 * re-entering its own HTTP stack at `route`, so `requireTenant` runs again, the audit event names the
 * real user, and suspended-tenant and step-up rules apply unchanged. There is no second authorization
 * path, and `assistant.test.ts` asserts that every `route` here exists in the route inventory with
 * exactly the `permission` declared next to it.
 *
 * Two consequences follow from the tools being permission-tagged:
 *  - the tool list is filtered per turn by the caller's role, so a viewer's model never *sees*
 *    `recover_files` and cannot offer it;
 *  - a tool can never be more powerful than the route behind it.
 *
 * `risk: 'confirm'` tools are never executed from the model's turn. They produce a proposal that the
 * person confirms against a server-rendered summary of the resolved arguments.
 */
import { z } from 'zod';
import { assessChange } from './change.js';
import { describePlan, planChangeSchema, vpathToNative } from './plan-change.js';
import { parsePolicySpec } from './policy.js';
import { roleCan, supportSessionCan } from './permissions.js';
import { SHARE_ASSISTANT_MAX_DAYS } from './share.js';
const uuid = z.string().uuid();
const vpath = z.string().min(1).max(4096);
/** Arguments are modelled on what a person would say, not on the shape of the route. */
const T = (spec) => spec;
// ───────────────────────── phase 1: reading ─────────────────────────
const readTools = [
    T({
        name: 'overview',
        description: 'The organization overview: how many computers are protected, how many need attention, backups in the last 24 hours, storage used, and open alerts. Start here for broad questions like "is everything OK?".',
        permission: 'tenant.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/dashboard',
        args: z.object({}),
        build: () => ({ path: '/api/t/:tenantId/dashboard' }),
    }),
    T({
        name: 'list_computers',
        description: 'Every computer and phone in the organization with its protection state, whether it is online, when it last backed up successfully, and which backup plan it uses. Use this to turn a name the person typed into an id.',
        permission: 'devices.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/devices',
        args: z.object({ search: z.string().max(200).optional().describe('Filter by computer name') }),
        build: (a) => ({ path: `/api/t/:tenantId/devices?limit=200${a.search ? `&q=${encodeURIComponent(a.search)}` : ''}` }),
    }),
    T({
        name: 'computer_details',
        description: 'One computer in detail: protection state, the backup plan in force, when the next backup is due, the most recent saved backup, and any backup running right now.',
        permission: 'devices.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/devices/:deviceId',
        args: z.object({ computerId: uuid }),
        build: (a) => ({ path: `/api/t/:tenantId/devices/${a.computerId}` }),
    }),
    T({
        name: 'list_backup_runs',
        description: 'Recent backup runs for one computer, newest first, with their outcome and any error. Use this for "why did last night fail?".',
        permission: 'jobs.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/devices/:deviceId/jobs',
        args: z.object({ computerId: uuid, limit: z.number().int().min(1).max(50).default(10) }),
        build: (a) => ({ path: `/api/t/:tenantId/devices/${a.computerId}/jobs?limit=${a.limit}` }),
    }),
    T({
        name: 'backup_run_details',
        description: 'One backup run in full, including its statistics, warnings and error text.',
        permission: 'jobs.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/jobs/:jobId',
        args: z.object({ runId: uuid }),
        build: (a) => ({ path: `/api/t/:tenantId/jobs/${a.runId}` }),
    }),
    T({
        name: 'computer_activity',
        description: 'What a computer reported recently: files it could not read, storage problems, updates. Useful when a backup finished with warnings.',
        permission: 'devices.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/devices/:deviceId/events',
        args: z.object({ computerId: uuid, limit: z.number().int().min(1).max(50).default(20) }),
        build: (a) => ({ path: `/api/t/:tenantId/devices/${a.computerId}/events?limit=${a.limit}` }),
    }),
    T({
        name: 'list_alerts',
        description: 'Alerts raised for this organization. Open alerts are the ones nobody has dealt with yet.',
        permission: 'alerts.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/alerts',
        args: z.object({ state: z.enum(['open', 'acknowledged', 'resolved', 'all']).default('open') }),
        build: (a) => ({ path: `/api/t/:tenantId/alerts?state=${a.state}` }),
        shape: shapeAlerts,
    }),
    T({
        name: 'list_backup_plans',
        description: 'The backup plans in this organization: what each one backs up (`covers`), when it runs, how long it keeps backups, whether it is paused, how many computers use it (`device_count`) and its `current_revision`.',
        permission: 'policies.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/policies',
        args: z.object({}),
        build: () => ({ path: '/api/t/:tenantId/policies' }),
        shape: (b) => ({ plans: (b.policies ?? []).map(shapePlan) }),
    }),
    T({
        name: 'plan_details',
        description: 'One backup plan in full: what it backs up and skips (`covers`), when it runs, how long it keeps backups, how many computers use it (the organization snapshot says which plan each computer is on), and its `current_revision`. Call it right before offering any change to a plan; the change must name that revision.',
        permission: 'policies.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/policies/:policyId',
        args: z.object({ planId: uuid }),
        build: (a) => ({ path: `/api/t/:tenantId/policies/${a.planId}` }),
        shape: (b) => ({
            plan: b.policy ? shapePlan(b.policy) : null,
            usage: b.usage,
            problems: b.problems,
        }),
    }),
    T({
        name: 'storage_usage',
        description: 'How much storage the organization is using against its plan allowance.',
        permission: 'usage.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/usage',
        args: z.object({}),
        build: () => ({ path: '/api/t/:tenantId/usage' }),
    }),
    T({
        name: 'list_saved_backups',
        description: 'The saved backups (restore points) kept for one computer, newest first. Every recovery starts by choosing one of these: its id is the `backupId` the other recovery tools need. Each one says how many files were added, changed and deleted since the one before, `unusualChange` when that was abnormal (the same rule that raises the "unusual changes" alert), whether it is being kept (`hold`), and whether an automatic test restore checked it (`verification_status`).',
        permission: 'catalog.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/devices/:deviceId/recovery-points',
        args: z.object({ computerId: uuid, limit: z.number().int().min(1).max(50).default(15) }),
        build: (a) => ({ path: `/api/t/:tenantId/devices/${a.computerId}/recovery-points?limit=${a.limit}` }),
        shape: shapeSavedBackups,
    }),
    T({
        name: 'find_file',
        description: 'Search one saved backup for files and folders whose name contains the text. This is how you locate something the person describes in words. Returns full paths, which are the only paths you may later recover.',
        permission: 'catalog.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/recovery-points/:rpId/search',
        args: z.object({ backupId: uuid, text: z.string().min(2).max(200) }),
        build: (a) => ({ path: `/api/t/:tenantId/recovery-points/${a.backupId}/search?q=${encodeURIComponent(a.text)}&limit=50` }),
    }),
    T({
        name: 'browse_backup',
        description: 'List one folder inside a saved backup. Use "/" for the top. Prefer find_file when the person knows part of the name.',
        permission: 'catalog.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/recovery-points/:rpId/browse',
        args: z.object({ backupId: uuid, folder: vpath.default('/') }),
        build: (a) => ({ path: `/api/t/:tenantId/recovery-points/${a.backupId}/browse?path=${encodeURIComponent(a.folder)}&limit=200` }),
    }),
    T({
        name: 'file_versions',
        description: 'Every kept version of one file across saved backups, newest first, with the backups each version appears in. Use this when the person wants the file "as it was" before a change.',
        permission: 'catalog.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/devices/:deviceId/versions',
        args: z.object({ computerId: uuid, path: vpath }),
        build: (a) => ({ path: `/api/t/:tenantId/devices/${a.computerId}/versions?path=${encodeURIComponent(a.path)}` }),
    }),
    T({
        name: 'estimate_recovery',
        description: 'How many files and how many bytes a recovery of these paths would move. This changes nothing. Always call it before proposing a recovery so the person sees a real number.',
        permission: 'catalog.read',
        risk: 'read',
        method: 'POST',
        route: '/api/t/:tenantId/restores/estimate',
        args: z.object({ backupId: uuid, paths: z.array(vpath).min(1).max(100) }),
        build: (a) => ({ path: '/api/t/:tenantId/restores/estimate', body: { recoveryPointId: a.backupId, paths: a.paths } }),
    }),
    T({
        name: 'list_recoveries',
        description: 'Recoveries that have been requested, newest first, with their progress and outcome.',
        permission: 'restores.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/restores',
        args: z.object({ limit: z.number().int().min(1).max(50).default(10) }),
        build: (a) => ({ path: `/api/t/:tenantId/restores?limit=${a.limit}` }),
    }),
    T({
        name: 'recovery_details',
        description: 'One recovery in detail: state, file counts, and where the files are going.',
        permission: 'restores.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/restores/:restoreId',
        args: z.object({ recoveryId: uuid }),
        build: (a) => ({ path: `/api/t/:tenantId/restores/${a.recoveryId}` }),
    }),
    T({
        name: 'recovery_problems',
        description: 'The files a recovery could not deliver, each with the error. Use this for "why did the recovery fail?" before offering to try again.',
        permission: 'restores.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/restores/:restoreId/items',
        args: z.object({ recoveryId: uuid }),
        build: (a) => ({ path: `/api/t/:tenantId/restores/${a.recoveryId}/items?state=failed&limit=50` }),
    }),
    T({
        name: 'skipped_files',
        description: 'The files one saved backup could not include, each with the reason in plain terms (in_use, no_permission, cloud_only, gone, missing_folder, bad_name, changed, disk_error, other), plus a count per reason. Use this for "why wasn\'t my file backed up?" and for a backup that finished with warnings.',
        permission: 'catalog.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/recovery-points/:rpId/skipped',
        args: z.object({ backupId: uuid }),
        build: (a) => ({ path: `/api/t/:tenantId/recovery-points/${a.backupId}/skipped?limit=50` }),
        // `path` is what a change or a recovery must quote; `onComputer` is how to say it to the person.
        shape: (b) => ({
            ...b,
            items: (b.items ?? []).map((i) => ({ ...i, onComputer: vpathToNative(String(i.path), b.pathStyle ?? 'posix') })),
        }),
    }),
    T({
        name: 'recently_deleted',
        description: 'Files that are gone from a computer or phone but are still in one of its saved backups, newest deletion first. Each one names the `backupId` that still holds it. Try this first when somebody says they deleted something. The paths it returns may be recovered.',
        permission: 'catalog.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/devices/:deviceId/deleted',
        args: z.object({ computerId: uuid, limit: z.number().int().min(1).max(100).default(50) }),
        build: (a) => ({ path: `/api/t/:tenantId/devices/${a.computerId}/deleted?limit=${a.limit}` }),
        shape: (b) => ({
            files: (b.files ?? []).map((f) => ({
                path: f.path,
                name: f.name,
                size: f.size,
                lastModified: f.mtime,
                goneSince: f.deleted_after,
                backupId: f.recoveryPointId,
            })),
        }),
    }),
    T({
        name: 'search_all_computers',
        description: 'Search the newest saved backup of every computer and phone at once for files and folders whose name contains the text. Use this when the person does not know which computer something was on. Each result names its computer and the `backupId` to recover from; the paths it returns may be recovered.',
        permission: 'catalog.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/search',
        args: z.object({ text: z.string().min(2).max(200) }),
        build: (a) => ({ path: `/api/t/:tenantId/search?q=${encodeURIComponent(a.text)}` }),
    }),
    T({
        name: 'activity_log',
        description: 'What happened in this organization recently and who did it, newest first: backups and recoveries requested, plans changed, alerts dealt with, people invited, automatic restore tests passed or failed. `action` narrows it by the start of the event name, e.g. "restore", "policy", "drill", "retention".',
        permission: 'audit.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/audit',
        args: z.object({ action: z.string().max(80).optional(), limit: z.number().int().min(1).max(50).default(30) }),
        build: (a) => ({ path: `/api/t/:tenantId/audit?limit=${a.limit}${a.action ? `&action=${encodeURIComponent(a.action)}` : ''}` }),
        shape: shapeActivity,
    }),
    T({
        name: 'who_can_help',
        description: 'The names of the administrators of this organization: the people who can do what this person may not. Call it whenever you are about to say that only an administrator can do something, so you can name them.',
        permission: 'users.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/members',
        args: z.object({}),
        build: () => ({ path: '/api/t/:tenantId/members' }),
        shape: shapeAdmins,
    }),
    T({
        name: 'my_notifications',
        description: 'The notifications this person has received in the portal, newest first, and which of them they have not read yet. Use this for "what did I miss?".',
        permission: 'tenant.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/notifications',
        args: z.object({}),
        build: () => ({ path: '/api/t/:tenantId/notifications' }),
        shape: (b) => ({
            unread: b.unread ?? 0,
            notifications: (b.notifications ?? []).slice(0, 30).map((n) => ({ title: n.title, body: n.body, when: n.created_at, read: !!n.read_at })),
        }),
    }),
    T({
        name: 'period_summary',
        description: 'The numbers for one calendar month, for a summary somebody can pass to the owner: backups completed and failed, the computers that failed most, recoveries, automatic restore tests passed and failed, storage at the start and end, and open alerts. `month` is "YYYY-MM"; leave it out for last month.',
        permission: 'reports.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/reports/summary',
        args: z.object({ month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).optional() }),
        build: (a) => {
            const { from, to } = monthRange(a.month);
            return { path: `/api/t/:tenantId/reports/summary?from=${from}&to=${to}` };
        },
    }),
];
/** First day of the month and first day of the next, as dates; the previous month when none is given. */
export function monthRange(month, now = new Date()) {
    let y;
    let m; // 0-based
    if (month)
        [y, m] = [Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1];
    else
        [y, m] = now.getUTCMonth() === 0 ? [now.getUTCFullYear() - 1, 11] : [now.getUTCFullYear(), now.getUTCMonth() - 1];
    const iso = (yy, mm) => new Date(Date.UTC(yy, mm, 1)).toISOString().slice(0, 10);
    return { from: iso(y, m), to: iso(y, m + 1) };
}
// ───────────────────────── what the model is shown ─────────────────────────
/** A plan in the terms of a change. The raw spec would be dropped by the size trim, and is not what to reason in. */
function shapePlan({ spec, ...p }) {
    let covers = null;
    try {
        covers = describePlan(parsePolicySpec(spec));
    }
    catch {
        // A plan that no longer parses is shown without its contents rather than failing the look-up.
    }
    return {
        id: p.id,
        name: p.name,
        current_revision: p.current_revision,
        paused: p.paused,
        is_default: p.is_default,
        device_count: p.device_count ?? p.effective_device_count,
        complete: p.complete,
        covers,
    };
}
/** Alert facts worth keeping. The rest of `details` is internal bookkeeping. */
const ALERT_FACTS = ['reasons', 'recoveryPointId', 'jobId', 'error', 'lastSuccessAt', 'lastSeenAt', 'percent', 'failures', 'drill'];
function shapeAlerts(b) {
    return {
        alerts: (b.alerts ?? []).map(({ details, ...a }) => {
            const d = (details ?? {});
            const facts = Object.fromEntries(ALERT_FACTS.filter((k) => d[k] !== undefined && d[k] !== null).map((k) => [k, d[k]]));
            // `details` itself would be dropped by the size trim, so what matters travels as `facts`. The
            // suspicious-change alert carries the flagged backup here, which is where a recovery starts.
            return { ...a, facts };
        }),
    };
}
function shapeSavedBackups(b) {
    return {
        recoveryPoints: (b.recoveryPoints ?? []).map(({ change_stats, ...rp }) => {
            const c = change_stats;
            const known = c && typeof c.added === 'number';
            return {
                ...rp,
                changes: known ? { added: c.added, modified: c.modified ?? 0, deleted: c.deleted ?? 0 } : null,
                unusualChange: known
                    ? assessChange({ added: c.added ?? 0, modified: c.modified ?? 0, deleted: c.deleted ?? 0, unchanged: c.unchanged ?? 0 }, Number(rp.new_bytes ?? 0), Number(rp.total_bytes ?? 0))
                    : [],
            };
        }),
    };
}
/** Names only: the model is told who can help, never how to reach them. */
function shapeAdmins(b) {
    const admins = (b.members ?? []).filter((m) => m.role === 'admin' && !m.disabled_at);
    const named = admins.map((m) => m.name?.trim()).filter((n) => !!n);
    return { administrators: named, administratorsWithoutAName: admins.length - named.length };
}
const ACTOR = { system: 'ZeroGap, automatically', operator: 'ZeroGap support', anonymous: 'somebody not signed in' };
/** Who did what, by name. Email and IP addresses stay on the Activity page. */
function shapeActivity(b) {
    return {
        events: (b.events ?? []).map((e) => ({
            when: e.occurred_at,
            what: e.action,
            outcome: e.outcome,
            who: e.actor_type === 'user' ? (e.actor_name ?? 'a member of the organization') : e.actor_type === 'device' ? `the computer ${String(e.device_name ?? '')}`.trim() : (ACTOR[String(e.actor_type)] ?? e.actor_type),
            computer: e.device_name ?? undefined,
            about: e.target_type ?? undefined,
            viaAssistant: e.details?.via === 'assistant' || undefined,
        })),
    };
}
// ───────────────────────── phase 2: actions that undo easily ─────────────────────────
const actionTools = [
    T({
        name: 'back_up_now',
        description: 'Start a backup of one computer straight away, instead of waiting for its schedule. It needs a complete backup plan, and is refused while another backup for that computer is running or waiting (check list_backup_runs first). If the computer is switched off, the request waits for it for up to a day and runs as soon as it is back; say so rather than refusing.',
        permission: 'jobs.manage',
        risk: 'confirm',
        method: 'POST',
        route: '/api/t/:tenantId/devices/:deviceId/backup',
        args: z.object({ computerId: uuid }),
        build: (a) => ({ path: `/api/t/:tenantId/devices/${a.computerId}/backup`, body: {} }),
        confirm: { action: 'Back up now' },
    }),
    T({
        name: 'cancel_backup',
        description: 'Stop a backup that is currently running or queued. Anything already uploaded is kept and reused next time.',
        permission: 'jobs.manage',
        risk: 'confirm',
        method: 'POST',
        route: '/api/t/:tenantId/jobs/:jobId/cancel',
        args: z.object({ runId: uuid }),
        build: (a) => ({ path: `/api/t/:tenantId/jobs/${a.runId}/cancel`, body: {} }),
        confirm: { action: 'Stop this backup' },
    }),
    T({
        name: 'pause_backup_plan',
        description: 'Pause a backup plan, so computers using it stop backing up on schedule until it is resumed. Existing backups are untouched.',
        permission: 'policies.manage',
        risk: 'confirm',
        method: 'POST',
        route: '/api/t/:tenantId/policies/:policyId/pause',
        args: z.object({ planId: uuid }),
        build: (a) => ({ path: `/api/t/:tenantId/policies/${a.planId}/pause`, body: {} }),
        confirm: { action: 'Pause this plan' },
    }),
    T({
        name: 'resume_backup_plan',
        description: 'Resume a paused backup plan so its computers go back to their schedule.',
        permission: 'policies.manage',
        risk: 'confirm',
        method: 'POST',
        route: '/api/t/:tenantId/policies/:policyId/resume',
        args: z.object({ planId: uuid }),
        build: (a) => ({ path: `/api/t/:tenantId/policies/${a.planId}/resume`, body: {} }),
        confirm: { action: 'Resume this plan' },
    }),
    T({
        name: 'settle_alert',
        description: 'Mark an alert as seen (acknowledge) or as dealt with (resolve). This only changes the alert, never a backup.',
        permission: 'alerts.manage',
        risk: 'confirm',
        method: 'POST',
        route: '/api/t/:tenantId/alerts/:alertId/:action',
        args: z.object({ alertId: uuid, as: z.enum(['acknowledge', 'resolve']) }),
        build: (a) => ({ path: `/api/t/:tenantId/alerts/${a.alertId}/${a.as}`, body: {} }),
        confirm: { action: 'Update this alert' },
    }),
    T({
        name: 'keep_backup',
        description: 'Keep one saved backup so it is never deleted on schedule, for example the last good one before something went wrong. It stays until somebody stops keeping it on the computer\'s own page. `reason` is a short note in the person\'s own words that is saved with it.',
        permission: 'retention.manage',
        risk: 'confirm',
        method: 'POST',
        route: '/api/t/:tenantId/recovery-points/:rpId/hold',
        args: z.object({ backupId: uuid, reason: z.string().min(3).max(300) }),
        // Always `hold: true`. Releasing a kept backup is the direction in which it can be lost, so it is
        // not expressible here; it stays a button on the computer's page.
        build: (a) => ({ path: `/api/t/:tenantId/recovery-points/${a.backupId}/hold`, body: { hold: true, reason: a.reason } }),
        confirm: { action: 'Keep this backup' },
    }),
    T({
        name: 'pause_cleanup',
        description: 'Stop old backups from being deleted on schedule, across the whole organization, for a number of days (1 to 30) while something is looked into. New backups carry on as usual. ZeroGap already does this by itself for 14 days when it raises an "unusual changes" alert.',
        permission: 'retention.manage',
        risk: 'confirm',
        method: 'POST',
        route: '/api/t/:tenantId/cleanup-pause',
        // Never 0: that is "go back to normal", which stays a button on the Overview page.
        args: z.object({ days: z.number().int().min(1).max(30), reason: z.string().min(3).max(300) }),
        build: (a) => ({ path: '/api/t/:tenantId/cleanup-pause', body: { days: a.days, reason: a.reason } }),
        confirm: { action: 'Keep old backups' },
    }),
    T({
        name: 'change_plan',
        description: 'Change a backup plan for every computer that uses it: add or remove folders or personal folders, skip exact files (paths exactly as a look-up returned them), skip patterns or file types, change when it runs, keep backups for longer, or rename it. Only what you list changes. It cannot shorten how long backups are kept, skip a whole folder the plan backs up, or use a pattern that matches everything. Call plan_details first and pass its current_revision as expectedRevision.',
        permission: 'policies.manage',
        risk: 'confirm',
        method: 'POST',
        route: '/api/t/:tenantId/policies/:policyId/change',
        args: z.object({ planId: uuid, expectedRevision: z.number().int().min(1), change: planChangeSchema }),
        build: (a) => ({ path: `/api/t/:tenantId/policies/${a.planId}/change`, body: { expectedRevision: a.expectedRevision, change: a.change } }),
        confirm: { action: 'Change the plan' },
    }),
    T({
        name: 'copy_plan_for_computers',
        description: 'Make a new backup plan as a copy of an existing one, with an optional change, and move the listed computers onto it; every other computer stays on the original plan. Use it when the person wants a change for only some of the computers that share a plan, or wants a new plan. `change` takes the same fields as change_plan. Call plan_details on the plan being copied first and pass its current_revision.',
        permission: 'policies.manage',
        risk: 'confirm',
        method: 'POST',
        route: '/api/t/:tenantId/policies/:policyId/change',
        args: z.object({
            fromPlanId: uuid,
            expectedRevision: z.number().int().min(1),
            name: z.string().trim().min(1).max(200),
            change: planChangeSchema.default({}),
            computerIds: z.array(uuid).max(20).default([]),
        }),
        build: (a) => ({
            path: `/api/t/:tenantId/policies/${a.fromPlanId}/change`,
            body: { expectedRevision: a.expectedRevision, change: a.change, saveAs: { name: a.name, deviceIds: a.computerIds } },
        }),
        confirm: { action: 'Make this plan' },
    }),
    T({
        name: 'move_computers_to_plan',
        description: 'Put computers on a different backup plan (each computer has exactly one). Use it for a computer with no plan, or to move one to a plan that suits it better.',
        permission: 'policies.manage',
        risk: 'confirm',
        method: 'POST',
        route: '/api/t/:tenantId/policies/:policyId/assign',
        args: z.object({ planId: uuid, computerIds: z.array(uuid).min(1).max(20) }),
        build: (a) => ({ path: `/api/t/:tenantId/policies/${a.planId}/assign`, body: { deviceIds: a.computerIds } }),
        confirm: { action: 'Move to this plan' },
    }),
];
// ───────────────────────── phase 3: getting files back ─────────────────────────
/**
 * The symbolic destination the assistant may name. The agent resolves it to a new dated folder under
 * the user's profile, the same way a phone mirror resolves its own root (agent/internal/agent/restore.go).
 * The assistant never composes an absolute path, so it cannot land files somewhere unexpected.
 */
export const ASSISTANT_RESTORE_DESTINATION = '@restored';
const recoveryTools = [
    T({
        name: 'recover_files',
        description: 'Get files back from a saved backup. Two deliveries are possible: "download" prepares a zip the person downloads in the browser (works even if the computer is switched off), and "to_computer" puts the files in a new dated folder on the computer they came from. Nothing is ever overwritten and nothing is written back over the originals. Only paths that came from find_file, browse_backup or file_versions in this conversation may be used. Call estimate_recovery first.',
        permission: 'restores.create',
        risk: 'confirm',
        method: 'POST',
        route: '/api/t/:tenantId/restores',
        args: z.object({
            computerId: uuid,
            backupId: uuid,
            paths: z.array(vpath).min(1).max(100),
            deliver: z.enum(['download', 'to_computer']),
        }),
        build: (a) => ({
            path: '/api/t/:tenantId/restores',
            body: {
                sourceDeviceId: a.computerId,
                recoveryPointId: a.backupId,
                paths: a.paths,
                // The two safe deliveries only. 'original' (write back over the source) is deliberately absent:
                // it is the one destination a person cannot undo, and it stays in the wizard where the warnings are.
                destinationType: a.deliver === 'download' ? 'browser' : 'alternate',
                destinationDeviceId: a.deliver === 'download' ? null : a.computerId,
                destinationPath: a.deliver === 'download' ? null : ASSISTANT_RESTORE_DESTINATION,
                // Never 'overwrite'. A name clash makes a second copy rather than destroying the local file.
                conflictPolicy: 'rename',
                restoreMetadata: true,
                restorePermissions: false,
                confirmOverwrite: false,
            },
        }),
        confirm: { action: 'Get these files back' },
    }),
    T({
        name: 'cancel_recovery',
        description: 'Stop a recovery that is still queued or running. Files already written stay where they are.',
        permission: 'restores.create',
        risk: 'confirm',
        method: 'POST',
        route: '/api/t/:tenantId/restores/:restoreId/cancel',
        args: z.object({ recoveryId: uuid }),
        build: (a) => ({ path: `/api/t/:tenantId/restores/${a.recoveryId}/cancel`, body: {} }),
        confirm: { action: 'Stop this recovery' },
    }),
    T({
        name: 'retry_recovery',
        description: 'Try a recovery to a computer again after it failed, stopped part-way or ran out of time. Only the files that did not arrive are sent again. A download cannot be retried: offer a new one instead.',
        permission: 'restores.create',
        risk: 'confirm',
        method: 'POST',
        route: '/api/t/:tenantId/restores/:restoreId/retry',
        args: z.object({ recoveryId: uuid }),
        build: (a) => ({ path: `/api/t/:tenantId/restores/${a.recoveryId}/retry`, body: {} }),
        confirm: { action: 'Try again' },
    }),
];
// ───────────────────────── shared links (docs/sharing.md) ─────────────────────────
/**
 * A shared link hands files to people outside the organization, so the assistant's part is narrow:
 *  - it never sees a link or a password (the routes' answers are shaped before it reads them, and a link
 *    made through it is shown to the person on the confirmation card only);
 *  - it never handles a password at all: the server makes one when the person wants one;
 *  - links it makes always end, within SHARE_ASSISTANT_MAX_DAYS;
 *  - it can make a link close sooner, never later or more open (docs/assistant.md A16). Removing a
 *    password, a later end date, or no end date stays on the link's own page.
 */
const SHARE_DAYS = SHARE_ASSISTANT_MAX_DAYS;
const shareTools = [
    T({
        name: 'list_archives',
        description: "A computer's folder archives: folders whose oldest files move to the cloud. Each has an id, the folder's path, and how many files are in the cloud. Use it to find files that are no longer on the computer itself.",
        permission: 'devices.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/devices/:deviceId/archives',
        args: z.object({ computerId: uuid }),
        build: (a) => ({ path: `/api/t/:tenantId/devices/${a.computerId}/archives` }),
    }),
    T({
        name: 'search_archive',
        description: 'Search one folder archive for files and folders whose name contains the text. Only results that are in the cloud (where "cloud" or "both") can be shared; the paths it returns may be shared.',
        permission: 'catalog.read',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/devices/:deviceId/archives/:archiveId/search',
        args: z.object({ computerId: uuid, archiveId: uuid, text: z.string().min(2).max(200) }),
        build: (a) => ({ path: `/api/t/:tenantId/devices/${a.computerId}/archives/${a.archiveId}/search?q=${encodeURIComponent(a.text)}` }),
    }),
    T({
        name: 'list_shared_links',
        description: 'The shared links of this organization: links that let people outside it see and download chosen files. For each: its name, whether it still works (status: active, stopped or ended, and why it ended), the computer and backup it came from, how many files, whether it has a password, until when it works, how many times it was opened and when last. You never see the links themselves.',
        permission: 'shares.create',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/shares',
        args: z.object({ onlyWorking: z.boolean().default(false).describe('Only links that still work') }),
        build: (a) => ({ path: `/api/t/:tenantId/shares?status=${a.onlyWorking ? 'active' : 'all'}` }),
        shape: shapeShares,
    }),
    T({
        name: 'shared_link_activity',
        description: 'Who did what with one shared link, newest first: when it was opened, which files were downloaded, wrong passwords, and changes made to it by people in the organization. Visitors are anonymous: say "someone with the link", and never guess who they were.',
        permission: 'shares.create',
        risk: 'read',
        method: 'GET',
        route: '/api/t/:tenantId/shares/:shareId/activity',
        args: z.object({ linkId: uuid }),
        build: (a) => ({ path: `/api/t/:tenantId/shares/${a.linkId}/activity?limit=100` }),
        shape: shapeShareActivity,
    }),
    T({
        name: 'create_shared_link',
        description: `Offer to make a link that lets anyone who has it see and download chosen files, without an account. Use paths exactly as find_file, browse_backup, search_all_computers or search_archive returned them in this conversation, from one saved backup (backupId) or one archive (archiveId). A folder shares everything in it. The link always ends, after endsInDays (1 to ${SHARE_DAYS}). password "generate" makes a password the person sends separately; recommend it for anything personal or financial. look "gallery" shows big pictures (photos), "list" shows names and folders (documents). The link and password are shown to the person when they confirm; you never see them and must never ask for them.`,
        permission: 'shares.create',
        risk: 'confirm',
        method: 'POST',
        route: '/api/t/:tenantId/shares',
        args: z
            .object({
            backupId: uuid.optional(),
            archiveId: uuid.optional(),
            paths: z.array(vpath).min(1).max(100),
            name: z.string().trim().min(1).max(120).describe('The title the person opening the link sees'),
            look: z.enum(['gallery', 'list']),
            endsInDays: z.number().int().min(1).max(SHARE_DAYS),
            password: z.enum(['generate', 'none']),
        })
            .refine((a) => !!a.backupId !== !!a.archiveId, 'Give either backupId or archiveId'),
        build: (a) => ({
            path: '/api/t/:tenantId/shares',
            body: {
                ...(a.backupId ? { recoveryPointId: a.backupId } : { folderArchiveId: a.archiveId }),
                paths: a.paths,
                name: a.name,
                theme: a.look,
                endsInDays: a.endsInDays,
                generatePassword: a.password === 'generate',
            },
        }),
        // The answer holds the link and the password: neither reaches the model or the transcript.
        shape: (b) => ({
            linkId: b.id,
            files: b.files,
            bytes: b.bytes,
            hasPassword: !!b.password,
            note: 'The link (and password, if any) were shown to the person on the card. You do not have them.',
        }),
        confirm: { action: 'Create the link' },
    }),
    T({
        name: 'change_shared_link',
        description: `Offer to rename a shared link, change its look, or make it end sooner (endsInDays from today, 1 to ${SHARE_DAYS}, and earlier than it ends now). It cannot make a link last longer, remove its password, or set a new one: that is done on the link's own page.`,
        permission: 'shares.create',
        risk: 'confirm',
        method: 'PATCH',
        route: '/api/t/:tenantId/shares/:shareId',
        args: z
            .object({
            linkId: uuid,
            name: z.string().trim().min(1).max(120).optional(),
            look: z.enum(['gallery', 'list']).optional(),
            endsInDays: z.number().int().min(1).max(SHARE_DAYS).optional(),
        })
            .refine((a) => a.name !== undefined || a.look !== undefined || a.endsInDays !== undefined, 'Say what to change'),
        build: (a) => ({
            path: `/api/t/:tenantId/shares/${a.linkId}`,
            body: { name: a.name, theme: a.look, endsInDays: a.endsInDays, onlyNarrow: true },
        }),
        confirm: { action: 'Save changes' },
    }),
    T({
        name: 'stop_shared_link',
        description: 'Offer to stop a shared link now: it stops working at once, also for anyone who has it open. The files themselves are not touched. Offer it when a link was sent to the wrong person, or the person who got it should no longer have it.',
        permission: 'shares.create',
        risk: 'confirm',
        method: 'POST',
        route: '/api/t/:tenantId/shares/:shareId/stop',
        args: z.object({ linkId: uuid }),
        build: (a) => ({ path: `/api/t/:tenantId/shares/${a.linkId}/stop`, body: {} }),
        confirm: { action: 'Stop sharing' },
    }),
];
/** A link as the model may know it: what it is and how it is used, never the link itself. */
function shapeShares(b) {
    return {
        links: (b.shares ?? []).slice(0, 100).map((s) => ({
            linkId: s.id,
            name: s.name,
            status: s.status,
            endedBecause: s.endReason ?? undefined,
            computer: s.deviceName,
            fromBackupSavedAt: s.backupTime ?? undefined,
            fromArchive: s.archivePath ?? undefined,
            files: s.files,
            bytes: s.bytes,
            hasPassword: s.hasPassword,
            worksUntil: s.expiresAt ?? 'until stopped',
            madeBy: s.createdBy ?? 'someone who has left',
            madeAt: s.createdAt,
            timesOpened: s.opens,
            downloads: s.downloads,
            lastOpenedAt: s.lastOpenedAt ?? undefined,
        })),
    };
}
/** Visits without their addresses or browsers; changes with the person's name. */
function shapeShareActivity(b) {
    return {
        events: (b.events ?? []).slice(0, 100).map((e) => {
            const d = (e.details ?? {});
            return {
                when: e.at,
                what: e.action,
                outcome: e.outcome,
                who: e.actorType === 'anonymous' ? 'someone with the link' : e.actorType === 'system' ? 'ZeroGap, automatically' : (e.actor ?? 'a member of the organization'),
                file: d.path ?? undefined,
                folder: d.folder ?? undefined,
                files: d.files ?? undefined,
                endedBecause: d.reason ?? undefined,
                viaAssistant: d.via === 'assistant' || undefined,
            };
        }),
    };
}
export const ASSISTANT_TOOLS = [...readTools, ...actionTools, ...recoveryTools, ...shareTools];
export const ASSISTANT_TOOL_NAMES = ASSISTANT_TOOLS.map((t) => t.name);
export function assistantTool(name) {
    return ASSISTANT_TOOLS.find((t) => t.name === name);
}
/**
 * The tools one caller may use this turn. A permission the caller does not hold removes the tool from
 * the model's list entirely, so it cannot be offered, described as available, or attempted.
 */
export function assistantToolsFor(access) {
    const can = (p) => (access.supportScope ? supportSessionCan(access.supportScope, p) : access.role ? roleCan(access.role, p) : false);
    return ASSISTANT_TOOLS.filter((t) => can(t.permission));
}
// ───────────────────────── pointing at a page ─────────────────────────
/**
 * The pages the assistant may send someone to.
 *
 * It names a page by key, never by address: `[[page:team]]`, which the portal renders as a button that
 * navigates there. Two reasons. A person who is told to "go to /users" has been handed a URL, which is
 * the sort of thing this product exists to keep away from them. And a closed list means the model cannot
 * invent a destination — an unknown key renders as nothing at all, not as a broken link.
 *
 * `label` is the portal's own name for the page, used to describe the page to the model. The button's
 * wording is the portal's business: it keeps its own translated map, keyed by `key` and checked for
 * completeness by the compiler (apps/web/src/assistant/text.tsx).
 */
export const ASSISTANT_PAGES = [
    { key: 'overview', path: '', label: 'Overview' },
    { key: 'computers', path: '/devices', label: 'Computers' },
    { key: 'add-computer', path: '/devices/add', label: 'Connect a computer' },
    { key: 'add-phone', path: '/devices/add-phone', label: 'Connect a phone' },
    { key: 'plans', path: '/plans', label: 'Backup plans' },
    { key: 'restore', path: '/restore', label: 'Get files back' },
    { key: 'recoveries', path: '/restores', label: 'Recoveries' },
    { key: 'alerts', path: '/alerts', label: 'Alerts' },
    { key: 'team', path: '/users', label: 'Team' },
    { key: 'activity', path: '/audit', label: 'Activity' },
    { key: 'billing', path: '/billing', label: 'Subscription & usage' },
    { key: 'settings', path: '/settings', label: 'Settings' },
    { key: 'shared-links', path: '/shares', label: 'Shared links' },
];
/** `[[page:team]]`, or `[[page:computer:<id>]]` for one computer's own page. */
export const ASSISTANT_PAGE_MARKER = /\[\[page:([a-z-]+)(?::([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}))?\]\]/g;
/**
 * Resolve one marker, or null when the model named something that is not a page. The id is re-checked
 * here rather than trusted from the marker's own pattern: this is what builds the address, so it is
 * where the shape has to hold.
 */
export function assistantPageLink(key, id) {
    if (key === 'computer')
        return id && UUID_RE.test(id) ? { key: 'computer', path: `/devices/${id}` } : null;
    const page = ASSISTANT_PAGES.find((p) => p.key === key);
    return page ? { key: page.key, path: page.path } : null;
}
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/**
 * Things people ask for that the assistant deliberately will not do, and where the portal does it
 * instead. Kept here so the model's refusal and the portal's button never drift apart.
 */
export const ASSISTANT_HANDOFFS = [
    { match: 'restore files back over the originals', why: 'it is the one recovery that cannot be undone', to: 'restore' },
    { match: 'add, remove, retire or rename a computer', why: 'it changes what is protected', to: 'computers' },
    { match: 'shorten how long backups are kept', why: 'it deletes the older backups, which cannot be undone', to: 'plans' },
    { match: 'change a plan’s advanced settings (speed limits, retries, time zone, checks)', why: 'they need the full form', to: 'plans' },
    { match: 'rotate or revoke a computer’s credentials', why: 'it is a security action that needs the full screen', to: 'computers' },
    { match: 'invite, remove or change the role of a person', why: 'it changes who can reach your backups', to: 'team' },
    { match: 'change the subscription, payment details or the plan', why: 'it changes what you are billed', to: 'billing' },
    { match: 'delete the organization or its data', why: 'it is irreversible', to: 'settings' },
    { match: 'stop keeping a saved backup', why: 'once it is no longer kept it can be deleted on schedule', to: 'computers' },
    { match: 'go back to deleting old backups on schedule', why: 'old backups start being deleted again', to: 'overview' },
    { match: 'change who gets emails or how often', why: 'each person sets that for themselves', to: 'settings' },
    { match: 'make a shared link last longer or never end, remove its password, or set a new password', why: 'it opens the files to more people, or for longer', to: 'shared-links' },
    { match: 'turn shared links on or off for the whole organization, or require passwords on them', why: 'it is a rule for everyone', to: 'settings' },
];
//# sourceMappingURL=assistant.js.map